import React, { useState, useEffect, useCallback } from "react";
import SettingsProfile from "./settingsProfile";
import SettingsWatermark from "./settingsWatermark";
import SettingsSubscription from "./settingsSubscription";
import SettingsTemplates from "./settingsTemplates";
import HelpHint from "../parts/helpHint";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Card,
  Tabs,
  Tab,
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import { API_URL } from "../../config";

function Settings(props) {
  const location = useLocation();
  const navigate = useNavigate();

  const [profileTab, setProfileTab] = useState(true);
  const [watermarkTab, setWatermarkTab] = useState(false);
  const [subscriptionTab, setSubscriptionTab] = useState(false);
  const [templatesTab, setTemplatesTab] = useState(false);
  const [paymentsTab, setPaymentsTab] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [templatesData, setTemplatesData] = useState([]);
  const [templating, setTemplating] = useState(true);
  const [templatesByName, setTemplatesByName] = useState([]);
  const [error, setError] = useState(null);

  const changeSelection = useCallback((hash) => {
    if (hash === "#profile") {
      setProfileTab(true);
      setWatermarkTab(false);
      setSubscriptionTab(false);
      setTemplatesTab(false);
      setPaymentsTab(false);
    } else if (hash === "#watermark") {
      setProfileTab(false);
      setWatermarkTab(true);
      setSubscriptionTab(false);
      setTemplatesTab(false);
      setPaymentsTab(false);
    } else if (hash === "#subscription") {
      setProfileTab(false);
      setWatermarkTab(false);
      setSubscriptionTab(true);
      setTemplatesTab(false);
      setPaymentsTab(false);
    } else if (hash === "#templates") {
      setProfileTab(false);
      setWatermarkTab(false);
      setSubscriptionTab(false);
      setTemplatesTab(true);
      setPaymentsTab(false);
    } else if (hash === "#payments") {
      setProfileTab(false);
      setWatermarkTab(false);
      setSubscriptionTab(false);
      setTemplatesTab(false);
      setPaymentsTab(true);
    }
  }, []);

  const fetchConnectStatus = useCallback(async () => {
    try {
      setConnectLoading(true);
      setConnectError(null);
      const token = props.token;
      if (!token) return;
      const appMetadata = data?.app_metadata || data?.user?.app_metadata || {};
      setConnectStatus({
        storeEnabled: !!appMetadata.storeEnabled,
        payoutsEnabled: !!appMetadata.payoutsEnabled,
      });
    } catch (e) {
      setConnectError(e?.message || "Unable to load Connect status");
    } finally {
      setConnectLoading(false);
    }
  }, [data, props.token]);

  // Initialize tab based on hash
  useEffect(() => {
    if (!location.hash) {
      navigate("/settings#profile", { replace: true });
    }
  }, [location.hash, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
    getTemplates();

    // Set initial tab based on hash
    const hash = location.hash || "#profile";
    changeSelection(hash);
  }, []);

  useEffect(() => {
    changeSelection(location.hash || "#profile");
  }, [changeSelection, location.hash]);

  useEffect(() => {
    if (paymentsTab) {
      fetchConnectStatus();
    }
  }, [paymentsTab, fetchConnectStatus]);

  const handleTabChange = useCallback((event, newValue) => {
    navigate(`/settings${newValue}`);
  }, [navigate]);

  const handleConnectOnboarding = useCallback(async () => {
    try {
      setConnectLoading(true);
      setConnectError(null);
      const token = props.token;
      const res = await fetch(API_URL + "/store/v1/connect/onboarding", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error("Failed to start Connect onboarding");
      const result = await res.json();
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (e) {
      setConnectError(e?.message || "Unable to start Connect onboarding");
    } finally {
      setConnectLoading(false);
    }
  }, [props.token]);

  const getData = useCallback(async () => {
    try {
      setError(null);
      const token = props.token;
      
      if (!token) {
        throw new Error("No authentication token available");
      }

      const body = JSON.stringify({
        type: "details",
      });

      const response = await fetch(API_URL + "/users/v1/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: body,
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(`Profile fetch failed: ${response.status} ${response.statusText}`);
      }

      const responseJson = await response.json();
      
      setData(responseJson);
      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching profile data:", err);
      setError(err.message);
      setIsLoading(false);
      toast.error(`Failed to load profile: ${err.message}`);
    }
  }, [props.token]);

  const getTemplates = useCallback(async () => {
    try {
      const token = props.token;
      
      if (!token) {
        console.warn("No token available for templates, using empty templates");
        setTemplatesData([]);
        setTemplatesByName([]);
        setTemplating(false);
        return;
      }

      // Since there's no dedicated templates endpoint, we'll extract templates from user profile
      // This assumes templates are stored in the user_metadata or app_metadata
      if (data && data.templates) {
        const templates = Object.entries(data.templates || {});
        const templatesByName = templates.map((item) => item[0]);

        setTemplatesData(templates);
        setTemplating(false);
        setTemplatesByName(templatesByName);
      } else {
        // Default empty template structure
        setTemplatesData([["default", "<p></p>"]]);
        setTemplatesByName(["default"]);
        setTemplating(false);
      }
    } catch (err) {
      console.error("Error processing templates:", err);
      toast.warn("Failed to load templates, using defaults");
      setTemplatesData([["default", "<p></p>"]]);
      setTemplatesByName(["default"]);
      setTemplating(false);
    }
  }, [props.token, data]);

  // Re-run getTemplates when data changes
  useEffect(() => {
    if (data && !templating) {
      getTemplates();
    }
  }, [data]);

  if (isLoading || templating) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="textSecondary">
          Loading settings...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">Error Loading Settings</Typography>
          <Typography>{error}</Typography>
        </Alert>
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <button 
            onClick={() => {
              setError(null);
              setIsLoading(true);
              getData();
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </Box>
      </Box>
    );
  }

  const tabHints = {
    "#profile": {
      title: "Profile Settings",
      hints: [
        "Update your profile details and logo",
        "Toggle monthly distribution on or off",
        "Switch between light and dark mode",
      ],
    },
    "#watermark": {
      title: "Watermark Settings",
      hints: [
        "Configure your default ShieldShot watermark",
        "Choose which store images get watermarked",
        "Toggle Subject Shield to protect the main subject",
      ],
    },
    "#subscription": {
      title: "Subscription Settings",
      hints: [
        "View your current subscription plan",
        "Manage billing and payment methods",
      ],
    },
    "#templates": {
      title: "Template Settings",
      hints: [
        "Create and edit email templates",
        "Preview templates before sending",
        "Set a default template for distributions",
      ],
    },
    "#payments": {
      title: "Payment Settings",
      hints: [
        "Connect your Stripe account for payouts",
        "View payment history and balance",
      ],
    },
  };

  const activeHash = location.hash || "#profile";
  const activeHints = tabHints[activeHash] || tabHints["#profile"];

  return (
    <Box id="contentArea" sx={{ pb: { xs: 10, sm: 6, md: 4 } }}>

      <Card elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", display: 'flex', alignItems: 'center' }} id="cardHeader">
          <Tabs
            value={activeHash}
            onChange={handleTabChange}
            aria-label="settings tabs"
            variant="fullWidth"
            sx={{
              flex: 1,
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              },
            }}
          >
            <Tab
              id="profileTab"
              label={
                <Typography sx={{ fontWeight: "bold" }}>Profile</Typography>
              }
              value="#profile"
            />
            <Tab
              id="watermarkTab"
              label={
                <Typography sx={{ fontWeight: "bold" }}>
                  Watermark
                </Typography>
              }
              value="#watermark"
            />
            <Tab
              id="subscriptionTab"
              label={
                <Typography sx={{ fontWeight: "bold" }}>
                  Subscription
                </Typography>
              }
              value="#subscription"
            />
            <Tab
              id="templatesTab"
              label={
                <Typography sx={{ fontWeight: "bold" }}>
                  Templates
                </Typography>
              }
              value="#templates"
            />
            <Tab
              id="paymentsTab"
              label={
                <Typography sx={{ fontWeight: "bold" }}>
                  Payments
                </Typography>
              }
              value="#payments"
            />
          </Tabs>
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
            <HelpHint
              title={activeHints.title}
              hints={activeHints.hints}
            />
          </Box>
        </Box>
        <Box p={3} id="cardBody">
          {profileTab && (
            <SettingsProfile
              token={props.token}
              isLoading={isLoading}
              data={data}
              AWS={props.AWS}
              user={props.user}
              onDataUpdate={getData}
            />
          )}
          {watermarkTab && (
            <SettingsWatermark token={props.token} user={props.user} />
          )}
          {subscriptionTab && (
            <SettingsSubscription
              token={props.token}
              data={data}
              onDataUpdate={getData}
            />
          )}
          {templatesTab && (
            <SettingsTemplates
              token={props.token}
              data={templatesData}
              isLoading={isLoading}
              metadata={data}
              user={props.user}
              templatesByName={templatesByName}
              onDataUpdate={getData}
            />
          )}
          {paymentsTab && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Stripe Connect
              </Typography>
              {connectError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {connectError}
                </Alert>
              )}
              {connectLoading && <CircularProgress sx={{ mb: 2 }} />}
              {connectStatus && !connectLoading && (
                <>
                  {connectStatus.storeEnabled ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Your store is enabled and ready to accept payments.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Complete Stripe Connect onboarding to start selling on ShieldShot.
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConnectOnboarding}
                    disabled={connectLoading}
                    sx={{ backgroundColor: "#635bff", "&:hover": { backgroundColor: "#5249d9" } }}
                  >
                    {connectStatus.storeEnabled
                      ? "Open Stripe Dashboard"
                      : "Complete Connect Onboarding"}
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
}

function SettingsWrapper(props) {
  const location = useLocation();
  return <Settings {...props} location={location} />;
}

export default SettingsWrapper;
